import {
  Alert,
  Card,
  Col,
  DataTable,
  Drawer,
  Row,
  Statistic,
  Tag,
  Typography,
  ViewButton,
} from '@wanjianshi/admin-ui';
import { formatDateTime, formatNumber } from '@wanjianshi/shared-utils';
import type { TableColumnsType } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/auth/store';
import { useAdminPage, useAdminRecord } from '@/hooks/useAdminResource';

type Identifier = string | number;

interface PersonalCreditLedgerRow {
  id: string;
  event_type: 'generation' | 'recharge' | 'redemption' | 'reversal';
  event_type_label: string;
  points_change: string;
  balance_before?: string | null;
  balance_after?: string | null;
  project_id?: Identifier | null;
  project_name?: string | null;
  task_id?: string | null;
  item_id?: string | null;
  node_id?: string | null;
  platform?: string | null;
  platform_id?: Identifier | null;
  model_id?: Identifier | null;
  resource_type: string;
  operation: string;
  gross_consumed_points: string;
  refunded_points: string;
  status: string;
  description?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  redeem_code_id?: Identifier | null;
  redeem_code?: string | null;
  pay_order_id?: Identifier | null;
  merchant_order_id?: string | null;
  order_no?: string | null;
  reversal_reason?: string | null;
  recharge_source?: string | null;
  bank_transaction_no?: string | null;
  received_date?: string | null;
  recharge_reason?: string | null;
  create_time?: string | null;
}

interface PersonalCreditConsumptionSummary {
  usage_gross_points: string;
  usage_refunded_points: string;
  usage_net_points: string;
  reversed_points: string;
  redeemed_points: string;
  total_consumed_points: string;
  usage_record_count: number;
  reversal_record_count: number;
  redemption_record_count: number;
}

interface FoundedTeamCreditConsumptionRow {
  id: Identifier;
  team_id: Identifier;
  team_name: string;
  team_status: number;
  team_deleted: boolean;
  user_id?: Identifier | null;
  user_nickname?: string | null;
  user_mobile_masked?: string | null;
  project_id?: Identifier | null;
  project_name?: string | null;
  task_id?: string | null;
  item_id?: string | null;
  type: 'used' | 'refunded';
  amount: number;
  consumed_points: number;
  refunded_points: number;
  balance_before: number;
  balance_after: number;
  description?: string | null;
  create_time?: string | null;
}

interface FoundedTeamConsumptionGroup {
  team_id: Identifier;
  team_name: string;
  consumed_points: string;
  refunded_points: string;
  net_consumed_points: string;
  record_count: number;
}

interface FoundedTeamConsumptionGroups {
  groups: FoundedTeamConsumptionGroup[];
  team_count: number;
  record_count: number;
  consumed_points: string;
  refunded_points: string;
  net_consumed_points: string;
}

const usageStatusLabels: Record<string, string> = {
  completed: '已完成',
  succeeded: '已完成',
  pending: '处理中',
  failed: '失败',
  refunded: '已退回',
  cancelled: '已退回',
};

const resourceTypeLabels: Record<string, string> = {
  image: '图片生成',
  image_edit: '图片编辑',
  image_lighting: '图片打光',
  image_matte: '图片抠图',
  image_transfer: '图片转换',
  video: '视频生成',
  audio: '音频生成',
  music: '音乐生成',
  llm_text: '文本生成',
  lip_sync: '口型同步',
  recharge: '积分充值',
};

const operationTypeLabels: Record<string, string> = {
  recharge: '积分充值',
  redeem_code: '兑换码到账',
  admin_recharge_point_reversal: '后台积分冲正',
};

function text(value: unknown): string {
  return String(value ?? '').trim() || '—';
}

function points(value: unknown): string {
  return formatNumber(String(value ?? 0));
}

function UsageStatusTag({ value }: { value: string }) {
  const color = value === 'succeeded' || value === 'completed'
    ? 'green'
    : value === 'pending'
      ? 'blue'
      : value === 'cancelled' || value === 'refunded'
        ? 'orange'
        : 'red';
  return <Tag color={color}>{usageStatusLabels[value] ?? text(value)}</Tag>;
}

function PanelError({ error, onRetry }: { error: string | null; onRetry: () => Promise<void> }) {
  return error
    ? <Alert showIcon type="error" message={error} action={<a onClick={() => void onRetry()}>重试</a>} />
    : null;
}

export function PlatformUserPersonalCreditConsumptions({ userId }: { userId: string }) {
  const summary = useAdminRecord<PersonalCreditConsumptionSummary>(
    `/admin/users/${userId}/credit-consumptions/summary`,
  );
  const resource = useAdminPage<PersonalCreditLedgerRow>(
    `/admin/users/${userId}/credit-consumptions`,
    20,
  );
  const columns = useMemo<TableColumnsType<PersonalCreditLedgerRow>>(() => [
    { title: '发生时间', dataIndex: 'create_time', key: 'create_time', fixed: 'left', width: 175, render: formatDateTime },
    {
      title: '流水类型', dataIndex: 'event_type', key: 'event_type', width: 120,
      render: (value, row) => {
        const color = value === 'recharge'
          ? 'cyan'
          : value === 'redemption'
            ? 'green'
            : value === 'reversal'
              ? 'orange'
              : 'blue';
        return <Tag color={color}>{row.event_type_label}</Tag>;
      },
    },
    {
      title: '积分变动', dataIndex: 'points_change', key: 'points_change', width: 125, align: 'right',
      render: value => {
        const amount = Number(value ?? 0);
        return (
          <Typography.Text strong type={amount < 0 ? 'danger' : amount > 0 ? 'success' : undefined}>
            {amount > 0 ? '+' : ''}{points(amount)}
          </Typography.Text>
        );
      },
    },
    {
      title: '余额变化', key: 'balance', width: 180,
      render: (_value, row) => row.balance_before != null && row.balance_after != null
        ? `${points(row.balance_before)} → ${points(row.balance_after)}`
        : '历史记录未保存余额',
    },
    { title: '状态', dataIndex: 'status', key: 'status', width: 105, render: value => <UsageStatusTag value={String(value)} /> },
    {
      title: '业务详情', key: 'business_detail', width: 290,
      render: (_value, row) => {
        if (row.event_type === 'redemption') {
          return (
            <div className="team-detail-stacked-text">
              <span>{row.redeem_code ? <Typography.Text copyable={{ text: row.redeem_code }}>{row.redeem_code}</Typography.Text> : '兑换码到账'}</span>
              <Typography.Text type="secondary">{row.redeem_code_id ? `兑换码 ID：${row.redeem_code_id}` : '历史兑换记录'}</Typography.Text>
            </div>
          );
        }
        if (row.event_type === 'reversal') {
          return (
            <div className="team-detail-stacked-text">
              <span>{row.merchant_order_id || row.order_no || '后台积分冲正'}</span>
              <Typography.Text type="secondary">{row.reversal_reason || row.description || '—'}</Typography.Text>
            </div>
          );
        }
        if (row.event_type === 'recharge') {
          const isManualRecharge = row.recharge_source === 'admin_manual_recharge';
          return (
            <div className="team-detail-stacked-text">
              <span>
                {row.bank_transaction_no
                  ? <Typography.Text copyable={{ text: row.bank_transaction_no }}>银行流水：{row.bank_transaction_no}</Typography.Text>
                  : isManualRecharge ? '后台手工充值' : '线上充值到账'}
              </span>
              <Typography.Text type="secondary">
                {[
                  row.received_date ? `到账日期：${row.received_date}` : null,
                  row.recharge_reason || row.description,
                ].filter(Boolean).join(' · ') || '积分已到账'}
              </Typography.Text>
            </div>
          );
        }
        return (
          <div className="team-detail-stacked-text">
            <span>{row.project_name || (row.project_id ? `项目 #${row.project_id}` : '未关联项目')}</span>
            <Typography.Text type="secondary">原始消耗 {points(row.gross_consumed_points)}，失败退回 {points(row.refunded_points)}</Typography.Text>
          </div>
        );
      },
    },
    {
      title: '任务 / 子任务', key: 'task', width: 230,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{text(row.task_id)}</span>
          <Typography.Text type="secondary">{row.item_id ? `子任务：${row.item_id}` : '—'}</Typography.Text>
        </div>
      ),
    },
    { title: '资源类型', dataIndex: 'resource_type', key: 'resource_type', width: 115, render: value => resourceTypeLabels[String(value)] ?? text(value) },
    { title: '操作类型', dataIndex: 'operation', key: 'operation', width: 140, render: value => operationTypeLabels[String(value)] ?? text(value) },
    { title: '模型 ID', dataIndex: 'model_id', key: 'model_id', width: 110, render: text },
    { title: '平台', dataIndex: 'platform', key: 'platform', width: 120, render: text },
    { title: '节点 ID', dataIndex: 'node_id', key: 'node_id', width: 150, render: text },
    { title: '说明 / 错误信息', key: 'description', width: 260, ellipsis: true, render: (_value, row) => text(row.error_message || row.description) },
  ], []);

  return (
    <div className="team-detail-panel-stack">
      <PanelError error={summary.error} onRetry={summary.reload} />
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={summary.loading && !summary.data}><Statistic title="个人累计积分扣减" value={summary.data?.total_consumed_points ?? 0} formatter={value => points(value)} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={summary.loading && !summary.data}><Statistic title="生成净消耗" value={summary.data?.usage_net_points ?? 0} formatter={value => points(value)} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={summary.loading && !summary.data}><Statistic title="后台积分冲正扣除" value={summary.data?.reversed_points ?? 0} formatter={value => points(value)} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={summary.loading && !summary.data}><Statistic title="兑换码累计到账" value={summary.data?.redeemed_points ?? 0} formatter={value => points(value)} /></Card></Col>
      </Row>

      <Card title={`个人积分流水（${resource.total}）`} className="management-list-card">
        <PanelError error={resource.error} onRetry={resource.reload} />
        <DataTable<PersonalCreditLedgerRow>
          columns={columns}
          dataSource={resource.items}
          loading={resource.loading}
          rowKey={row => String(row.id)}
          scrollY="max(120px, calc(100vh - 486px))"
          pagination={{ current: resource.page, pageSize: resource.pageSize, total: resource.total }}
          onChange={pagination => {
            const nextPageSize = pagination.pageSize ?? resource.pageSize;
            if (nextPageSize !== resource.pageSize) resource.setPageSize(nextPageSize);
            resource.setPage(pagination.current ?? 1);
          }}
        />
      </Card>
    </div>
  );
}

export function PlatformUserFoundedTeamCreditConsumptions({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const canViewTeam = useAdminAuth(state => state.hasPermission('wjs-admin:team:detail'));
  const [selectedTeam, setSelectedTeam] = useState<FoundedTeamConsumptionGroup | null>(null);
  const groups = useAdminRecord<FoundedTeamConsumptionGroups>(
    `/admin/users/${userId}/founded-team-credit-consumptions/groups`,
  );
  const details = useAdminPage<FoundedTeamCreditConsumptionRow>(
    selectedTeam
      ? `/admin/users/${userId}/founded-team-credit-consumptions?team_id=${selectedTeam.team_id}`
      : null,
    20,
  );
  const detailColumns = useMemo<TableColumnsType<FoundedTeamCreditConsumptionRow>>(() => [
    { title: '发生时间', dataIndex: 'create_time', key: 'create_time', fixed: 'left', width: 175, render: formatDateTime },
    {
      title: '流水类型', dataIndex: 'type', key: 'type', width: 105,
      render: value => value === 'refunded' ? <Tag color="orange">失败退回</Tag> : <Tag color="red">团队消耗</Tag>,
    },
    {
      title: '积分变动', dataIndex: 'amount', key: 'amount', width: 110, align: 'right',
      render: value => {
        const amount = Number(value ?? 0);
        return <Typography.Text strong type={amount < 0 ? 'danger' : 'success'}>{amount > 0 ? '+' : ''}{points(amount)}</Typography.Text>;
      },
    },
    {
      title: '操作成员', key: 'operator', width: 180,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{row.user_nickname || (row.user_id ? `用户 #${row.user_id}` : '系统')}</span>
          <Typography.Text type="secondary">{text(row.user_mobile_masked)}</Typography.Text>
        </div>
      ),
    },
    { title: '项目', dataIndex: 'project_name', key: 'project_name', width: 180, render: (_value, row) => row.project_name || (row.project_id ? `项目 #${row.project_id}` : '—') },
    {
      title: '任务 / 子任务', key: 'task', width: 230,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{text(row.task_id)}</span>
          <Typography.Text type="secondary">{row.item_id ? `子任务：${row.item_id}` : '—'}</Typography.Text>
        </div>
      ),
    },
    { title: '余额变化', key: 'balance', width: 165, render: (_value, row) => `${points(row.balance_before)} → ${points(row.balance_after)}` },
    { title: '说明', dataIndex: 'description', key: 'description', width: 240, ellipsis: true, render: text },
  ], []);

  const groupColumns = useMemo<TableColumnsType<FoundedTeamConsumptionGroup>>(() => [
    {
      title: '团队', key: 'team', fixed: 'left', width: 240,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <Typography.Text strong>{row.team_name}</Typography.Text>
          <Typography.Text type="secondary">团队 ID：{row.team_id}</Typography.Text>
        </div>
      ),
    },
    { title: '累计消耗', dataIndex: 'consumed_points', key: 'consumed_points', width: 130, align: 'right', render: points },
    { title: '失败退回', dataIndex: 'refunded_points', key: 'refunded_points', width: 130, align: 'right', render: points },
    { title: '净消耗积分', dataIndex: 'net_consumed_points', key: 'net_consumed_points', width: 140, align: 'right', render: value => <Typography.Text strong type="danger">{points(value)}</Typography.Text> },
    { title: '流水数', dataIndex: 'record_count', key: 'record_count', width: 100, align: 'right', render: points },
    {
      title: '操作', key: 'action', fixed: 'right', width: 180,
      render: (_value, row) => (
        <>
          <ViewButton onClick={() => setSelectedTeam(row)}>消耗明细</ViewButton>
          {canViewTeam ? <ViewButton onClick={() => navigate(`/teams/${row.team_id}`)}>团队详情</ViewButton> : null}
        </>
      ),
    },
  ], [canViewTeam, navigate]);

  return (
    <div className="team-detail-panel-stack">
      <PanelError error={groups.error} onRetry={groups.reload} />
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={groups.loading && !groups.data}><Statistic title="全部团队净消耗" value={groups.data?.net_consumed_points ?? 0} formatter={value => points(value)} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={groups.loading && !groups.data}><Statistic title="全部团队累计消耗" value={groups.data?.consumed_points ?? 0} formatter={value => points(value)} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={groups.loading && !groups.data}><Statistic title="全部团队失败退回" value={groups.data?.refunded_points ?? 0} formatter={value => points(value)} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card size="small" className="management-metric-card" loading={groups.loading && !groups.data}><Statistic title="发生消耗的团队" value={groups.data?.team_count ?? 0} suffix="个" /></Card></Col>
      </Row>

      <Card title="按团队汇总" className="management-list-card">
        <DataTable<FoundedTeamConsumptionGroup>
          columns={groupColumns}
          dataSource={groups.data?.groups ?? []}
          loading={groups.loading}
          rowKey={row => String(row.team_id)}
          scrollY="max(120px, calc(100vh - 430px))"
          pagination={false}
        />
      </Card>

      <Drawer
        title={selectedTeam ? `${selectedTeam.team_name} · 积分消耗明细` : '团队积分消耗明细'}
        size={1180}
        open={Boolean(selectedTeam)}
        className="platform-user-team-credit-drawer"
        onClose={() => setSelectedTeam(null)}
      >
        {selectedTeam ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col span={8}><Card size="small"><Statistic title="累计消耗" value={selectedTeam.consumed_points} formatter={value => points(value)} /></Card></Col>
              <Col span={8}><Card size="small"><Statistic title="失败退回" value={selectedTeam.refunded_points} formatter={value => points(value)} /></Card></Col>
              <Col span={8}><Card size="small"><Statistic title="净消耗" value={selectedTeam.net_consumed_points} formatter={value => points(value)} /></Card></Col>
            </Row>
            <PanelError error={details.error} onRetry={details.reload} />
            <DataTable<FoundedTeamCreditConsumptionRow>
              columns={detailColumns}
              dataSource={details.items}
              loading={details.loading}
              rowKey={row => String(row.id)}
              scrollY="max(120px, calc(100vh - 430px))"
              pagination={{ current: details.page, pageSize: details.pageSize, total: details.total }}
              onChange={pagination => {
                const nextPageSize = pagination.pageSize ?? details.pageSize;
                if (nextPageSize !== details.pageSize) details.setPageSize(nextPageSize);
                details.setPage(pagination.current ?? 1);
              }}
            />
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
